/**
 * Messagerie (tasks/messagerie-plan.md) — contrat partagé par l'API, le
 * campus web et la future app mobile : formes des réponses de
 * `/v1/messaging`, événements de la socket, limites, codes d'erreur.
 * Sous-chemin `@campus/core/messaging` côté serveur, jamais le barrel.
 */

export type ConversationKind = 'general' | 'profile' | 'direct' | 'group';

export const MESSAGE_MAX_LENGTH = 4000;
/** Nom affiché d'un invité (le seul nom qu'on garde de lui). */
export const GUEST_NAME_MAX_LENGTH = 50;
export const CLIENT_ID_MAX_LENGTH = 64;
/** Messages par page (historique). */
export const MESSAGES_PAGE_SIZE = 50;
export const MESSAGES_PAGE_MAX = 100;
/** Envoi : messages par personne et par fenêtre. */
export const MESSAGE_RATE_LIMIT = { max: 15, windowMs: 10_000 };
/** Réactions : barre rapide sous la souris (pouce, cœur, mort de rire, bravo, merci, fait). */
export const QUICK_REACTIONS = ['thumbs-up', 'heart', 'joy', 'party', 'thanks', 'check'] as const;
/** Réaction du double-clic sur un message (le « like » rapide). */
export const LIKE_REACTION = 'thumbs-up';
/** Emojis différents par personne et par message. */
export const REACTIONS_PER_USER_MAX = 20;
/** Réactions ajoutées ou retirées par personne et par fenêtre. */
export const REACTION_RATE_LIMIT = { max: 40, windowMs: 10_000 };
/** Longueur de l'extrait d'un message cité. */
export const REPLY_PREVIEW_LENGTH = 140;
/** Groupes : nombre de membres (créateur compris) et longueur du nom. */
export const GROUP_MAX_MEMBERS = 50;
export const GROUP_TITLE_MAX_LENGTH = 60;
/** Non-lus affichés au plus (« 99+ »). */
export const UNREAD_CAP = 99;

// ── Fichiers (§ 5 du plan) ──────────────────────────────────

export type AttachmentKind = 'image' | 'video' | 'audio' | 'voice' | 'file';

/** Taille max par type (octets). */
export const ATTACHMENT_MAX_BYTES: Record<AttachmentKind, number> = {
  image: 20 * 1024 * 1024,
  video: 200 * 1024 * 1024,
  audio: 50 * 1024 * 1024,
  voice: 20 * 1024 * 1024,
  file: 50 * 1024 * 1024,
};
/** Message vocal : durée maximale (le serveur refuse au-delà, l'enregistrement s'arrête seul). */
export const VOICE_MAX_MS = 5 * 60_000;
/** Formats d'enregistrement essayés dans l'ordre (le serveur convertit tout en M4A/AAC). */
export const VOICE_RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'] as const;

/** Plus gros envoi possible (refus avant la fin du transfert au-delà). */
export const ATTACHMENT_UPLOAD_MAX_BYTES = 200 * 1024 * 1024;
export const ATTACHMENTS_PER_MESSAGE = 10;
/** Envois de fichiers par personne et par heure. */
export const ATTACHMENT_RATE_LIMIT = { max: 30, windowMs: 60 * 60_000 };
/** Place pour les fichiers de la messagerie, par tenant. */
export const MESSAGING_QUOTA_BYTES = 5 * 1024 * 1024 * 1024;
/** Côté largeur ou hauteur au-delà duquel une image est réduite. */
export const IMAGE_MAX_SIDE = 2560;
export const ATTACHMENT_NAME_MAX_LENGTH = 200;

/**
 * Formats acceptés (attribut `accept` d'un champ fichier). Le serveur juge sur
 * le contenu du fichier, jamais sur l'extension : cette liste n'est qu'un filtre
 * de confort côté appareil.
 */
export const ATTACHMENT_ACCEPT = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'video/mp4', 'video/quicktime', 'video/webm',
  'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/ogg', 'audio/webm', 'audio/wav', 'audio/x-wav',
  'application/pdf', 'text/plain', 'text/csv',
  '.docx', '.xlsx', '.pptx', '.odt', '.ods', '.odp', '.txt', '.csv', '.mov', '.m4a', '.mp3',
].join(',');

export interface AttachmentDTO {
  id: string;
  kind: AttachmentKind;
  mime: string;
  name: string;
  size: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** 64 valeurs de 0 à 255 (audio). */
  waveform: number[] | null;
  /** Aperçu (image, vidéo) : `GET /attachments/:id/preview`. */
  hasPreview: boolean;
}

export interface MessageReactionDTO {
  /** Identifiant du catalogue `@campus/core/reactions`. */
  reactionId: string;
  /** Qui a réagi, du premier au dernier (le nombre = la longueur). */
  userIds: string[];
}

/** Message cité au-dessus d'une réponse (aperçu court). */
export interface ReplyPreviewDTO {
  id: string;
  senderId: string;
  senderName: string;
  /** Début du texte (REPLY_PREVIEW_LENGTH caractères), vide si supprimé. */
  text: string;
  /** Supprimé, masqué par la modération ou plus lisible. */
  deleted: boolean;
  /** Fichiers du message cité (« Photo », « Message vocal 0:12 »), null s'il n'en a pas. */
  attachment: { kind: AttachmentKind; durationMs: number | null; count: number } | null;
}

/**
 * Ce qui s'est passé dans un groupe (message système, au nom de celui qui l'a
 * fait). `names` : noms des personnes concernées au moment des faits, dans le
 * même ordre que `userIds` (l'annuaire fait foi tant qu'elles y sont).
 */
export type GroupEvent =
  | { type: 'created'; title: string }
  | { type: 'renamed'; title: string }
  | { type: 'added'; userIds: string[]; names: string[] }
  | { type: 'removed'; userIds: string[]; names: string[] }
  | { type: 'left' }
  /** Nommé(s) administrateur(s) du groupe. */
  | { type: 'admin-added'; userIds: string[]; names: string[] }
  | { type: 'admin-removed'; userIds: string[]; names: string[] }
  /** Devenu administrateur d'office : le dernier administrateur est parti. */
  | { type: 'promoted'; userIds: string[]; names: string[] };

export interface MessageDTO {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  /** Vide pour un message supprimé. */
  text: string;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  clientId: string | null;
  /** Fichiers joints (vide pour un message supprimé). */
  attachments: AttachmentDTO[];
  /** Réactions, dans l'ordre de leur première apparition (vide pour un message supprimé). */
  reactions: MessageReactionDTO[];
  /** Message auquel celui-ci répond (même conversation). */
  replyTo: ReplyPreviewDTO | null;
  /** Message système d'un groupe (texte vide), null pour un message ordinaire. */
  system: GroupEvent | null;
}

export interface ConversationDTO {
  id: string;
  kind: ConversationKind;
  /** Général : « Général » ; profil : nom du profil ; privé : null (nom de l'autre, dans l'annuaire). */
  title: string | null;
  /** Fil de la personne (son général, ou le fil de son profil) : affiché en premier. */
  home: boolean;
  /** Membres (privé, groupe) ; vide pour le général et un profil. Groupe : du plus ancien au plus récent. */
  memberIds: string[];
  /** Groupe : ses administrateurs. */
  adminIds: string[];
  lastMessage: MessageDTO | null;
  /** Non-lus, plafonnés à UNREAD_CAP. */
  unread: number;
  /** Privé : jusqu'où l'autre a lu (« Vu »). */
  otherLastReadAt: string | null;
  /** Privé : jusqu'où les messages sont arrivés sur un appareil de l'autre (« Reçu »). */
  otherDeliveredAt: string | null;
}

export interface MessagePage {
  /** Du plus ancien au plus récent. */
  messages: MessageDTO[];
  /** Curseur pour la page plus ancienne (`before`), null s'il n'y en a plus. */
  olderCursor: string | null;
  /** Curseur pour la page plus récente (`after`), null si l'on est à la fin. */
  newerCursor: string | null;
}

export type MessagingErrorCode =
  | 'GUEST_FORBIDDEN' | 'EMPTY_MESSAGE' | 'MESSAGE_TOO_LONG' | 'SELF_MESSAGE'
  | 'RECIPIENT_NOT_FOUND' | 'RATE_LIMITED' | 'DM_CONNECTION_REQUIRED'
  | 'CONVERSATION_NOT_FOUND' | 'MESSAGE_NOT_FOUND' | 'NOT_AUTHOR' | 'MESSAGE_DELETED'
  | 'INVALID_CURSOR' | 'INVALID_REQUEST'
  | 'UNSUPPORTED_FILE' | 'FILE_TOO_LARGE' | 'QUOTA_EXCEEDED' | 'TOO_MANY_FILES' | 'ATTACHMENT_NOT_FOUND'
  | 'FILE_UNREADABLE' | 'ATTACHMENT_EXPIRED' | 'VOICE_TOO_LONG'
  | 'UNKNOWN_REACTION' | 'TOO_MANY_REACTIONS' | 'REPLY_NOT_FOUND'
  | 'INVALID_TITLE' | 'GROUP_FULL' | 'NOT_GROUP_ADMIN' | 'NOT_A_GROUP' | 'NOT_A_MEMBER'
  | 'GIFS_DISABLED' | 'GIF_NOT_FOUND' | 'GIF_UNAVAILABLE';

// ── GIF (recherche Giphy, passée par l'API) ─────────────────────

/** Un GIF trouvé. `previewUrl` : aperçu servi par l'API (adresse signée, relative à l'API). */
export interface GifResult {
  id: string;
  title: string;
  width: number;
  height: number;
  previewUrl: string;
}

/** `next` : décalage de la page suivante, null en fin de liste. */
export interface GifPage {
  results: GifResult[];
  next: number | null;
}

// ── Socket (`GET /v1/messaging/socket`) ─────────────────────────

/** Messages envoyés par l'appareil. Le premier doit être `auth` (le jeton n'est jamais dans l'adresse). */
export type MessagingClientFrame =
  | { type: 'auth'; token: string }
  | { type: 'ping' }
  | { type: 'typing'; conversationId: string };

/** Messages envoyés par le serveur. */
export type MessagingServerFrame =
  | { type: 'ready' }
  | { type: 'pong' }
  | { type: 'error'; code: 'AUTH_REQUIRED' | 'AUTH_FAILED' }
  | MessagingEvent;

/** Événements diffusés (Redis `msg:{tenantId}:c:{conversationId}` et `msg:{tenantId}:u:{userId}`). */
export type MessagingEvent =
  | { type: 'message.created'; message: MessageDTO }
  | { type: 'message.updated'; message: MessageDTO }
  /** Réactions d'un message après un ajout ou un retrait (liste complète). */
  | { type: 'reactions.updated'; conversationId: string; messageId: string; reactions: MessageReactionDTO[] }
  | { type: 'read.updated'; conversationId: string; userId: string; lastReadAt: string }
  /** Privé : messages arrivés sur un appareil de cette personne jusqu'à `deliveredAt` (« Reçu »). */
  | { type: 'delivered.updated'; conversationId: string; userId: string; deliveredAt: string }
  | { type: 'typing'; conversationId: string; userId: string }
  /** Une conversation est apparue pour cette personne (premier message privé reçu, ajout à un groupe). */
  | { type: 'conversation.added'; conversationId: string }
  /** Groupe renommé, membres ou administrateurs changés. */
  | { type: 'conversation.updated'; conversation: GroupInfo }
  /** Cette personne n'est plus dans la conversation (partie, retirée, groupe supprimé). */
  | { type: 'conversation.removed'; conversationId: string; reason?: 'deleted' };

/** État d'un groupe diffusé à ses membres. */
export interface GroupInfo {
  id: string;
  title: string;
  memberIds: string[];
  adminIds: string[];
}

/** Fermetures de socket sans reconnexion automatique. */
export const SOCKET_CLOSE = {
  AUTH_FAILED: 4401,
  /** Session révoquée, rôle ou accès changé : nouveau jeton nécessaire. */
  REVOKED: 4403,
} as const;

/** L'appareil envoie un ping au moins toutes les N ms ; sans nouvelles pendant le double, le serveur ferme. */
export const SOCKET_PING_INTERVAL_MS = 25_000;
/** Un « en train d'écrire » dure tant qu'il est renouvelé avant ce délai. */
export const TYPING_TTL_MS = 5_000;

export function directKey(a: string, b: string): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}
